// Where the screens get their data. In development (no VITE_API_URL) the app
// runs on the mock below, which returns exactly the shapes the contract
// promises, so screens built now work unchanged against the real API.
import { createLedgerClient, unwrap, type Schemas } from '@elysian/api-client';
import { clientInfoHeader } from '../auth/clientInfo';
import { mock } from './mock';

export type Dashboard = Schemas['Dashboard'];
export type AgedBalance = Schemas['AgedBalance'];
export type BankAccountSummary = Schemas['BankAccountSummary'];
export type NewBankAccount = Schemas['NewBankAccount'];
export type InboundAddress = Schemas['InboundAddress'];
export type NumberSequence = Schemas['NumberSequence'];
export type ContactTransactions = Schemas['ContactTransactions'];
export type ContactImportRow = Schemas['ContactImportRow'];
export type CashDocument = Schemas['CashDocument'];
export type RefundInput = Schemas['RefundInput'];
export type OpenItemRow = Schemas['OpenItemRow'];
export type OpenItemsInput = Schemas['OpenItemsInput'];
export type OpenItemsCheck = Schemas['OpenItemsCheck'];
export type SageCompareInput = Schemas['SageCompareInput'];
export type SageCompare = Schemas['SageCompare'];
export type SageCompareAged = Schemas['SageCompareAged'];
export type SageCompareRow = Schemas['SageCompareRow'];
export type ImportMapping = Schemas['ImportMapping'];
export type ImportMappingColumns = Schemas['ImportMappingColumns'];
export type ImportMappingInput = Schemas['ImportMappingInput'];
export type InvoiceImportLine = Schemas['InvoiceImportLine'];
export type InvoiceImportInput = Schemas['InvoiceImportInput'];
export type InvoiceImportCheck = Schemas['InvoiceImportCheck'];
export type BankTransactions = Schemas['BankTransactions'];
export type DocumentList = Schemas['DocumentList'];
export type DocumentListTab = keyof DocumentList['counts'];
export type DocumentSort = 'number' | 'contact' | 'issued' | 'due' | 'total' | 'outstanding';
export type DocumentListQuery = { side: 'sales' | 'purchases'; tab?: DocumentListTab; q?: string; from?: string; to?: string;
  sort?: DocumentSort; dir?: 'asc' | 'desc'; limit?: number; offset?: number };
export type BankTransactionsQuery = { from?: string; to?: string; unreconciled?: boolean; limit?: number; offset?: number };
export type RecordCashInput = Schemas['RecordCashInput'];
export type ContactImportRequest = Schemas['ContactImportRequest'];
export type ContactImportResult = Schemas['ContactImportResult'];
export type ContactTransaction = ContactTransactions['lines'][number];
export type NumberingPatch = Partial<Record<NumberSequence['key'], { prefix?: string; next_value?: number; digits?: number }>>;
export type AccountBalance = Schemas['AccountBalance'];
export type OrganisationContext = Schemas['OrganisationContext'];
export type Account = Schemas['Account'];
export type TaxCode = Schemas['TaxCode'];
export type VatCode = Schemas['VatCode'];
export type Product = Schemas['Product'];
export type ProductInput = Schemas['ProductInput'];
export type ProductImportResult = Schemas['ProductImportResult'];
export type ContactProduct = Schemas['ContactProduct'];
export type ContactProductInput = Schemas['ContactProductInput'];
export type VatCodeList = Schemas['VatCodeList'];
export type NewVatCode = Schemas['NewVatCode'];
export type VatCodePatch = Schemas['VatCodePatch'];
export type Contact = Schemas['Contact'];
export type DocumentSummary = Schemas['DocumentSummary'];
export type LedgerDocument = Schemas['Document'];
export type DocumentInput = Schemas['DocumentInput'];
export type DocumentLineInput = Schemas['DocumentLineInput'];
export type DocumentType = Schemas['DocumentType'];
export type DocumentStatus = Schemas['DocumentStatus'];
export type EmailMessage = Schemas['EmailMessage'];
export type SendInput = Schemas['SendInput'];
export type DocumentTemplate = Schemas['DocumentTemplate'];
export type TemplateSettings = Schemas['TemplateSettings'];
export type TemplateKind = DocumentTemplate['kind'];
export type TemplateLayout = DocumentTemplate['layout'];

export type InboxItem = Schemas['InboxItem'];
export type InboxStatus = Schemas['InboxStatus'];
export type InboxUsage = Schemas['InboxUsage'];
export type BankLine = Schemas['BankLine'];
export type MatchSuggestion = Schemas['MatchSuggestion'];
export type ReconcileAction = Schemas['ReconcileAction'];
export type ReconciliationStatus = Schemas['ReconciliationStatus'];
export type BankRule = Schemas['BankRule'];
export type BankImportResult = Schemas['BankImportResult'];
export type VatReturn = Schemas['VatReturn'];
export type RosVat3 = Schemas['RosVat3'];
export type FiscalYear = Schemas['FiscalYear'];
export type BookExport = Schemas['BookExport'];
export type SearchResults = Schemas['SearchResults'];
export type ShellStatus = Schemas['ShellStatus'];
export type Period = Schemas['Period'];
export type ChecklistItem = Schemas['ChecklistItem'];
export type AuditEntry = Schemas['AuditEntry'];
export type AuditPage = Schemas['AuditPage'];
export type AuditPerson = Schemas['AuditPerson'];
export type AuditKind = Schemas['AuditKind'];
/** Filters for the audit trail; dates are Irish-time days, `before` the previous page's next_before. */
export type AuditFilter = { person?: string; kind?: AuditKind; from?: string; to?: string; q?: string; entity?: string; before?: number; limit?: number };
export type RecurringTemplate = Schemas['RecurringTemplate'];
export type RecurringRun = Schemas['RecurringRun'];
export type StatementSchedule = Schemas['StatementSchedule'];
export type StatementRequest = Schemas['StatementRequest'];
export type StatementRunResult = Schemas['StatementRunResult'];
export type MemberList = Schemas['MemberList'];
export type NewInvitation = Schemas['NewInvitation'];
export type Role = Schemas['Role'];
export type ImportBatch = Schemas['ImportBatch'];
export type ImportEntity = Schemas['ImportEntity'];
export type OpeningBalanceStatus = Schemas['OpeningBalanceStatus'];
export type AccountMapEntry = Schemas['AccountMapEntry'];
export type Backup = Schemas['Backup'];
export interface SageStatus { connected: boolean; business_name?: string | null; connected_at?: string | null; expires_at?: string | null; last_pulled_at?: string | null }
export interface SagePull { as_at?: string; batches: { entity?: ImportEntity; rows?: number; id?: string | null }[] }
export type ContactInput = Schemas['ContactInput'];
export type ContactPerson = Schemas['ContactPerson'];
/** The zero id: on a contact change, puts them back on the default invoice layout (#43). */
export const ZERO_ID = '00000000-0000-0000-0000-000000000000';
export type NewAccount = Schemas['NewAccount'];
export type AccountPatch = { name?: string; default_tax_code?: string | null; is_active?: boolean };
export type Journal = Schemas['Journal'];
export type JournalInput = Schemas['JournalInput'];
export type JournalSource = Schemas['JournalSource'];
export type Attachment = Schemas['Attachment'];
export type BrandAsset = Schemas['BrandAsset'];
export type BankDetails = Schemas['BankDetails'];
/** Where an uploaded file goes: the bill inbox (default), or onto a document or journal. */
export type UploadTarget = { to_inbox?: boolean; document_id?: string; journal_id?: string };
export type UploadResult = { attachment_id: string; inbox_id?: string | null; is_new: boolean };
export type Me = Schemas['Me'];
export type Membership = Schemas['Membership'];
export type InvitationPreview = Schemas['InvitationPreview'];
export type NewOrganisation = Schemas['NewOrganisation'];
export type Organisation = Schemas['Organisation'];
export type OrganisationSettings = Schemas['OrganisationSettings'];
export type OrganisationPatch = Schemas['OrganisationPatch'];
export type FxRate = Schemas['FxRate'];
export type FxOverview = Schemas['FxOverview'];
export type Revaluation = Schemas['Revaluation'];
export type FxFeedProvider = 'wise' | 'revolut';
export type FxFeedCredentials =
  | { provider: 'wise'; token: string }
  | { provider: 'revolut'; client_id: string; refresh_token: string; private_key_pem: string; issuer: string };
export type HmrcStatus = Schemas['HmrcStatus'];
export type MtdObligation = Schemas['MtdObligation'];
export type MtdObligations = Schemas['MtdObligations'];
export type MtdVatReturn = Schemas['MtdVatReturn'];
export type MtdReceipt = Schemas['MtdReceipt'];
export type BankFeedConnection = Schemas['BankFeedConnection'];
export type BankFeedAccount = Schemas['BankFeedAccount'];
export type FeedInstitution = Schemas['FeedInstitution'];
export type BankFeedSyncResult = { account_code?: string; added?: number; skipped?: number; connection_id?: string; error?: string; renew?: boolean };
export type SupplierChoice = { contact_id: string } | { create_from_extraction: true };

export interface DocumentQuery {
  type?: DocumentType;
  status?: DocumentStatus;
  overdue?: boolean;
  contactId?: string;
}

export interface LedgerSource {
  organisation(): Promise<OrganisationContext>;
  /** The signed-in person and the books they can open. */
  me(): Promise<Me>;
  createOrganisation(input: NewOrganisation): Promise<Organisation>;
  /** Settings → Organisation: everything, with what can't change now and why. */
  organisationSettings(): Promise<OrganisationSettings>;
  /** Change only the settings sent; problems come back in plain words. */
  updateOrganisation(patch: OrganisationPatch): Promise<OrganisationSettings>;
  /** Prefixes, lengths and next numbers for invoices, credit notes, bills and journals (#13). */
  numbering(): Promise<NumberSequence[]>;
  /** Change only the kinds sent; numbers never go backwards. Needs settings.accounts. */
  setNumbering(patch: NumberingPatch): Promise<NumberSequence[]>;
  /** Public: who an invitation is from, before signing in. */
  invitationPreview(token: string): Promise<InvitationPreview>;
  acceptInvitation(token: string): Promise<Organisation>;
  dashboard(): Promise<Dashboard>;
  agedDebtors(asAt: string): Promise<AgedBalance[]>;
  /** With includeInactive, the switched-off accounts too, and why active ones can't be switched off. */
  bankAccounts(includeInactive?: boolean): Promise<BankAccountSummary[]>;
  /** Add a bank account (#18); the code is chosen when left out. Switch one off or on with updateAccount. */
  createBankAccount(input: NewBankAccount): Promise<BankAccountSummary>;
  profitAndLoss(from: string, to: string): Promise<AccountBalance[]>;

  accounts(cls?: Schemas['AccountClass'], includeInactive?: boolean): Promise<Account[]>;
  createAccount(input: NewAccount): Promise<Account>;
  updateAccount(code: string, patch: AccountPatch): Promise<Account>;
  journals(q: { source?: JournalSource; account?: string; from?: string; to?: string }): Promise<Journal[]>;
  createJournal(input: JournalInput): Promise<Journal>;
  reverseJournal(id: string, date: string, reason: string): Promise<Journal>;
  taxCodes(on: string): Promise<TaxCode[]>;
  /** Products and services: the price list. */
  products(includeInactive?: boolean): Promise<Product[]>;
  /** Add or change an item by its code; settings left out keep their value. */
  saveProduct(code: string, input: ProductInput): Promise<Product>;
  setProductActive(code: string, active: boolean): Promise<Product>;
  deleteProduct(code: string): Promise<void>;
  /** Many at once: nothing is saved unless every row is fine. */
  importProducts(rows: ProductInput[]): Promise<ProductImportResult>;
  /** A customer's own prices, codes and descriptions for items. */
  contactProducts(contactId: string): Promise<ContactProduct[]>;
  /** Set them for one item; all three empty goes back to the list's. */
  setContactProduct(contactId: string, code: string, input: ContactProductInput): Promise<ContactProduct[]>;
  /** Settings → VAT codes: every code with its rates and boxes; each change returns the list again. */
  vatCodes(on?: string): Promise<VatCodeList>;
  createVatCode(input: NewVatCode): Promise<VatCodeList>;
  updateVatCode(code: string, patch: VatCodePatch): Promise<VatCodeList>;
  setVatCodeActive(code: string, active: boolean): Promise<VatCodeList>;
  deleteVatCode(code: string): Promise<VatCodeList>;
  /** A new rate (a percentage, "13.5") from a day. */
  setVatRate(code: string, rate: string, from: string): Promise<VatCodeList>;
  removeVatRate(code: string, from: string): Promise<VatCodeList>;
  contacts(role: 'customer' | 'supplier'): Promise<Contact[]>;
  contact(id: string): Promise<Contact>;
  /** A customer's or supplier's transactions with a running balance (#11); from the beginning when no from. */
  contactTransactions(id: string, q?: { from?: string; to?: string; side?: 'customer' | 'supplier' }): Promise<ContactTransactions>;
  createContact(input: ContactInput): Promise<Contact>;
  updateContact(id: string, input: ContactInput): Promise<Contact>;
  /** Customers or suppliers read from another package's export: checked, or imported when apply is true (#16). */
  importContacts(req: ContactImportRequest): Promise<ContactImportResult>;
  /** Unpaid invoices, credit notes and bills from another package's export: checked, or staged as import batches with apply. */
  checkOpenItems(input: OpenItemsInput): Promise<OpenItemsCheck>;
  /** #41: invoices and credit notes from another system's CSV, checked, or saved as drafts with apply. */
  checkInvoiceImport(input: InvoiceImportInput): Promise<InvoiceImportCheck>;
  invoiceMappings(): Promise<ImportMapping[]>;
  saveInvoiceMapping(input: ImportMappingInput): Promise<{ id: string }>;
  deleteInvoiceMapping(id: string): Promise<void>;
  /** Money received from a customer, entered by hand, into a bank account; allocated, the rest on account (#26). */
  recordReceipt(input: RecordCashInput): Promise<CashDocument>;
  /** Money paid to a supplier, entered by hand (#26). */
  recordPayment(input: RecordCashInput): Promise<CashDocument>;
  /** Receipts (payments) with money still on the contact's account; use them with allocateCredit. */
  onAccount(contactId: string): Promise<CashDocument[]>;
  /** One receipt, payment or refund. */
  cashDocument(id: string): Promise<CashDocument>;
  /** Change what a receipt pays: the whole new set; anything taken off needs a reason and is held on account (#26). */
  reallocateCash(id: string, allocations: { document_id: string; amount: string }[], reason?: string): Promise<CashDocument>;
  /** Undo a receipt, payment or refund entered by mistake (not while it's matched in the bank). */
  undoCash(id: string, date: string, reason: string): Promise<CashDocument>;
  /** Pay credit held on account back to the customer; returns the refund. */
  refundOnAccount(id: string, input: RefundInput): Promise<CashDocument>;
  allocateCredit(creditId: string, allocations: { document_id: string; amount: string }[]): Promise<LedgerDocument>;
  trialBalance(asAt: string): Promise<AccountBalance[]>;
  balanceSheet(asAt: string): Promise<AccountBalance[]>;
  agedCreditors(asAt: string): Promise<AgedBalance[]>;
  documents(query: DocumentQuery): Promise<DocumentSummary[]>;
  /** #49, #50: the Invoices or Bills list filtered, sorted, counted and paged on the server. */
  documentList(q: DocumentListQuery): Promise<DocumentList>;
  document(id: string): Promise<LedgerDocument>;
  createDraft(input: DocumentInput): Promise<LedgerDocument>;
  replaceDraft(id: string, input: DocumentInput): Promise<LedgerDocument>;
  deleteDraft(id: string): Promise<void>;
  /** Post a draft; with paidFrom (a bank, card or cash account code) a bill or invoice is paid there and then. */
  postDocument(id: string, paidFrom?: string): Promise<LedgerDocument>;
  voidDocument(id: string, date: string, reason: string): Promise<LedgerDocument>;
  /** A posted invoice with a mistake: a credit note for all of it, and a new draft with its lines to correct. */
  creditAndReissue(id: string, date: string, reason: string): Promise<{ credit_note: LedgerDocument; draft: LedgerDocument }>;
  sendDocument(id: string, input: SendInput): Promise<EmailMessage>;
  templates(): Promise<DocumentTemplate[]>;
  saveTemplate(kind: TemplateKind, layout: TemplateLayout, settings: TemplateSettings): Promise<DocumentTemplate>;
  /** #43: a named invoice layout, copied from another (the default when not given). */
  createTemplate(name: string, copyFrom?: string): Promise<DocumentTemplate>;
  updateTemplate(id: string, patch: { name?: string; layout?: TemplateLayout; settings?: TemplateSettings }): Promise<DocumentTemplate>;
  deleteTemplate(id: string): Promise<void>;
  /** The PDF exactly as the customer receives it; null when there is no server (demo data). */
  pdf(id: string): Promise<Blob | null>;

  inbox(status?: InboxStatus): Promise<{ items: InboxItem[]; usage: InboxUsage }>;
  /** The books' address for bills (#22), made on first look. */
  inboxAddress(): Promise<InboundAddress>;
  /** A new address for bills; the old one stops working. Needs settings.accounts. */
  newInboxAddress(allowedSenders?: string[]): Promise<InboundAddress>;
  inboxItem(id: string): Promise<InboxItem>;
  setInboxSupplier(id: string, choice: SupplierChoice): Promise<InboxItem>;
  approveInbox(id: string, acknowledgeWarnings: boolean): Promise<LedgerDocument>;
  rejectInbox(id: string, reason: string): Promise<void>;
  /** A bill that couldn't be scanned or read goes through the pipeline again. */
  retryInbox(id: string): Promise<InboxItem>;
  /** The stored bill file; null when there is no server (demo data). */
  attachmentFile(attachmentId: string): Promise<Blob | null>;
  uploadAttachment(file: File, target?: UploadTarget): Promise<UploadResult>;
  /** Take a file off a draft (files on posted records and journals stay). */
  unlinkAttachment(attachmentId: string, documentId: string): Promise<void>;
  brandAssets(): Promise<BrandAsset[]>;
  uploadBrandAsset(file: File, kind: BrandAsset['kind']): Promise<{ id: string; width_px: number; height_px: number }>;
  brandAssetFile(id: string): Promise<Blob | null>;
  setBankDetails(code: string, details: BankDetails | null): Promise<BankAccountSummary>;

  /** Open-banking feeds: connections, the accounts each shares, and which bank account each feeds. */
  bankFeeds(): Promise<{ configured: boolean; connections: BankFeedConnection[] }>;
  feedInstitutions(country?: string): Promise<FeedInstitution[]>;
  /** Where to send the browser to give consent at the bank. */
  connectBankFeed(institution: string, country?: string): Promise<{ authorise_url: string }>;
  linkBankFeedAccount(id: string, accountCode: string | null): Promise<BankFeedConnection[]>;
  syncBankFeeds(): Promise<{ results: BankFeedSyncResult[] }>;
  disconnectBankFeed(id: string): Promise<void>;

  bankLines(code: string, status?: BankLine['status']): Promise<BankLine[]>;
  /** #51: the books' entries on a bank account, with a running balance and whether each is reconciled. */
  bankTransactions(code: string, q?: BankTransactionsQuery): Promise<BankTransactions>;
  /** #53: reconcile against a paper or PDF statement; refused unless the balance agrees. */
  reconcileByHand(code: string, input: { statement_date: string; closing_balance: string; journal_lines: string[] }): Promise<{ id: string; statement_date: string; closing_balance: number; entries: number }>;
  untickByHand(code: string, journalLineId: string, reason: string): Promise<void>;
  bankSuggestions(lineId: string): Promise<MatchSuggestion[]>;
  reconcile(lineId: string, action: ReconcileAction): Promise<BankLine>;
  unmatch(lineId: string): Promise<BankLine>;
  reconciliation(code: string, asAt: string): Promise<ReconciliationStatus>;
  signOff(code: string, asAt: string): Promise<ReconciliationStatus>;
  bankRules(): Promise<BankRule[]>;
  createBankRule(rule: BankRule): Promise<BankRule>;
  uploadStatement(code: string, file: File): Promise<BankImportResult>;

  vatReturns(): Promise<VatReturn[]>;
  calculateVat(from: string, to: string): Promise<VatReturn>;
  rosVat3(from: string, to: string): Promise<RosVat3>;
  /** The VAT3 figures as a CSV for the accountant; null with demo data. */
  rosVat3Csv(from: string, to: string): Promise<Blob | null>;
  fileVat(from: string, to: string, reference: string): Promise<VatReturn>;

  fiscalYears(): Promise<FiscalYear[]>;
  /** The books for an auditor: OECD SAF-T XML. Logged. */
  exportSaft(from: string, to: string): Promise<Blob>;
  /** The books for the accountant: a zip of spreadsheets. Logged. */
  exportAccountantPack(from: string, to: string): Promise<Blob>;
  bookExports(): Promise<BookExport[]>;
  /** Contacts, documents, journals and accounts matching a name, number, reference or amount. */
  search(q: string, limit?: number): Promise<SearchResults>;
  /** Menu counts, this financial year, connected apps. */
  status(): Promise<ShellStatus>;
  yearEndChecklist(id: string): Promise<ChecklistItem[]>;
  closeYear(id: string, acknowledgeWarnings: boolean): Promise<FiscalYear>;
  reopenYear(id: string, reason: string): Promise<FiscalYear>;
  setPeriodStatus(id: string, status: Period['status']): Promise<Period>;
  /** What's still open in a month: bank, inbox, drafts, VAT, suspense, recurring, earlier months. */
  monthEndChecklist(periodId: string): Promise<ChecklistItem[]>;
  /** Close a month through its checklist; refused with the open items unless acknowledged. */
  closePeriod(id: string, acknowledgeWarnings: boolean): Promise<Period>;
  /** Who changed what and when, newest first, a page at a time. */
  auditTrail(filter?: AuditFilter): Promise<AuditPage>;
  /** The people and connected apps that appear in the audit trail. */
  auditPeople(): Promise<AuditPerson[]>;

  createStatements(req: StatementRequest): Promise<StatementRunResult>;
  statementSchedules(): Promise<StatementSchedule[]>;
  createStatementSchedule(s: StatementSchedule): Promise<StatementSchedule>;
  recurring(): Promise<RecurringTemplate[]>;
  createRecurring(t: RecurringTemplate): Promise<RecurringTemplate>;
  runRecurringNow(id: string): Promise<RecurringRun>;
  recurringRuns(status?: RecurringRun['status']): Promise<RecurringRun[]>;
  approveRun(id: string, send: boolean): Promise<RecurringRun>;
  declineRun(id: string, skip: boolean, note?: string): Promise<RecurringRun>;

  members(): Promise<MemberList>;
  invite(inv: NewInvitation): Promise<{ invitation_id: string; email_sent: boolean; accept_url?: string | null }>;
  revokeInvitation(id: string): Promise<void>;

  imports(): Promise<{ batches: ImportBatch[]; opening_balances: OpeningBalanceStatus }>;
  importBatch(id: string): Promise<ImportBatch>;
  setAccountMap(id: string, map: AccountMapEntry[]): Promise<ImportBatch>;
  validateImport(id: string): Promise<ImportBatch>;
  commitImport(id: string, createMissingContacts: boolean): Promise<ImportBatch>;
  reverseImport(id: string, reason: string): Promise<ImportBatch>;
  /** Cutover step 10: an imported trial balance, and Sage's aged reports, against the Ledger line by line (db/054). */
  compareWithSage(id: string, input: SageCompareInput): Promise<SageCompare>;
  uploadImport(file: File, entity: ImportEntity, sourceFormat: string, asAt?: string, profitToCapital?: boolean): Promise<ImportBatch>;
  /** An upload not yet imported, thrown away (db/045). */
  discardImport(id: string): Promise<void>;
  sageStatus(): Promise<SageStatus>;
  sageConnect(): Promise<{ authorise_url: string }>;
  sageDisconnect(): Promise<void>;
  sagePull(asAt: string): Promise<SagePull>;

  backups(): Promise<Backup[]>;
  requestBackup(reason?: string): Promise<Backup>;
  /** The encrypted backup file; null with demo data. */
  downloadBackup(id: string): Promise<Blob | null>;
  restoreBackup(id: string, choice: { mode: 'copy'; new_name: string } | { mode: 'replace'; confirm_name: string }): Promise<{ job_id: string }>;

  /** Where exchange rates come from, the currencies in use and typed-in rates. */
  fxOverview(asAt?: string): Promise<FxOverview>;
  setFxSettings(settings: { source?: 'ecb' | 'hmrc'; feed?: 'none' | FxFeedProvider; currencies?: string[] }): Promise<FxOverview>;
  /** The rate for a currency on a day (base per 1 unit) and where it came from. */
  fxRate(currency: string, date: string): Promise<FxRate>;
  /** A typed-in rate wins over every feed for that day; null removes it. */
  setFxRate(currency: string, date: string, rate: number | null): Promise<void>;
  connectFxFeed(credentials: FxFeedCredentials): Promise<FxOverview>;
  disconnectFxFeed(): Promise<void>;
  revaluationPreview(asAt: string): Promise<Revaluation>;
  postRevaluation(asAt: string): Promise<Revaluation>;

  /** Making Tax Digital (UK books): the HMRC connection. */
  hmrcStatus(): Promise<HmrcStatus>;
  /** Where to send the browser to sign in to HMRC. */
  connectHmrc(): Promise<{ authorise_url: string }>;
  disconnectHmrc(): Promise<void>;
  /** HMRC's periods (open, and filed in the last year), with the nine boxes for each finished one. */
  mtdObligations(): Promise<MtdObligations>;
  /** Send the return with the declaration; HMRC's receipt comes back and the months lock. */
  submitMtdReturn(periodKey: string, from: string, to: string): Promise<MtdReceipt>;
  mtdReceipt(periodKey: string): Promise<MtdReceipt>;
}

export function apiSource(baseUrl: string, getToken: () => string | null | Promise<string | null>, getOrgId: () => string | null,
  onUnauthorized?: () => void): LedgerSource {
  const api = createLedgerClient({ baseUrl, getToken, getOrgId, onUnauthorized });
  const org = () => ({ 'Elysian-Org': getOrgId() ?? '' });
  const doc = (id: string) => ({ header: org(), path: { id } });
  return {
    organisation: () => unwrap(api.GET('/organisation', { params: { header: org() } })),
    me: () => unwrap(api.GET('/me')),
    createOrganisation: (input) => unwrap(api.POST('/organisations', { body: input })),
    organisationSettings: () => unwrap(api.GET('/organisation/settings', { params: { header: org() } })),
    updateOrganisation: (patch) => unwrap(api.PATCH('/organisation', { params: { header: org() }, body: patch })),
    numbering: () => unwrap(api.GET('/numbering', { params: { header: org() } })),
    setNumbering: (patch) => unwrap(api.PATCH('/numbering', { params: { header: org() }, body: patch })),
    invitationPreview: (token) => unwrap(api.GET('/invitations/preview', { params: { query: { token } } })),
    acceptInvitation: (token) => unwrap(api.POST('/invitations/accept', { body: { token } })),
    dashboard: () => unwrap(api.GET('/dashboard', { params: { header: org() } })),
    agedDebtors: (asAt) =>
      unwrap(api.GET('/reports/aged', { params: { header: org(), query: { kind: 'debtors', as_at: asAt } } })),
    bankAccounts: (includeInactive) => unwrap(api.GET('/bank-accounts', { params: { header: org(), query: includeInactive ? { include_inactive: true } : {} } })),
    createBankAccount: (input) => unwrap(api.POST('/bank-accounts', { params: { header: org() }, body: input })),
    profitAndLoss: (from, to) =>
      unwrap(api.GET('/reports/profit-and-loss', { params: { header: org(), query: { from, to } } })),

    accounts: (cls, includeInactive) => unwrap(api.GET('/accounts', { params: { header: org(), query: { class: cls, include_inactive: includeInactive } } })),
    createAccount: (input) => unwrap(api.POST('/accounts', { params: { header: org() }, body: input })),
    updateAccount: (code, patch) => unwrap(api.PATCH('/accounts/{code}', { params: { header: org(), path: { code } }, body: patch })),
    // Every page, up to 5,000 (#49): the list used to stop at the first 500 without saying so.
    journals: async (q) => {
      const all: Journal[] = [];
      let cursor: string | undefined;
      do {
        const page = await unwrap(api.GET('/journals', { params: { header: org(), query: { ...q, limit: 500, ...(cursor ? { cursor } : {}) } } }));
        all.push(...page.items);
        cursor = page.next_cursor ?? undefined;
      } while (cursor && all.length < 5000);
      return all;
    },
    createJournal: (input) => unwrap(api.POST('/journals', { params: { header: org() }, body: input })),
    reverseJournal: (id, date, reason) => unwrap(api.POST('/journals/{id}/reverse', { params: doc(id), body: { date, reason } })),
    taxCodes: (on) => unwrap(api.GET('/tax-codes', { params: { header: org(), query: { on } } })),
    products: (includeInactive) => unwrap(api.GET('/products', { params: { header: org(), query: includeInactive ? { include_inactive: true } : {} } })),
    saveProduct: (code, input) => unwrap(api.PUT('/products/{code}', { params: { header: org(), path: { code } }, body: input })),
    setProductActive: (code, active) => unwrap(api.PUT('/products/{code}/active', { params: { header: org(), path: { code } }, body: { active } })),
    deleteProduct: async (code) => { await unwrap(api.DELETE('/products/{code}', { params: { header: org(), path: { code } } })); },
    importProducts: (rows) => unwrap(api.POST('/products/import', { params: { header: org() }, body: { rows } })),
    contactProducts: (id) => unwrap(api.GET('/contacts/{id}/products', { params: doc(id) })),
    setContactProduct: (id, code, input) => unwrap(api.PUT('/contacts/{id}/products/{code}', { params: { header: org(), path: { id, code } }, body: input })),
    vatCodes: (on) => unwrap(api.GET('/vat-codes', { params: { header: org(), query: on ? { on } : {} } })),
    createVatCode: (input) => unwrap(api.POST('/vat-codes', { params: { header: org() }, body: input })),
    updateVatCode: (code, patch) => unwrap(api.PATCH('/vat-codes/{code}', { params: { header: org(), path: { code } }, body: patch })),
    setVatCodeActive: (code, active) => unwrap(api.PUT('/vat-codes/{code}/active', { params: { header: org(), path: { code } }, body: { active } })),
    deleteVatCode: (code) => unwrap(api.DELETE('/vat-codes/{code}', { params: { header: org(), path: { code } } })),
    setVatRate: (code, rate, from) => unwrap(api.POST('/vat-codes/{code}/rates', { params: { header: org(), path: { code } }, body: { rate, from } })),
    removeVatRate: (code, from) => unwrap(api.DELETE('/vat-codes/{code}/rates/{from}', { params: { header: org(), path: { code, from } } })),
    contacts: async (role) => {
      const all: Contact[] = [];
      let cursor: string | undefined;
      do {
        const page = await unwrap(api.GET('/contacts', { params: { header: org(), query: { role, limit: 500, ...(cursor ? { cursor } : {}) } } }));
        all.push(...page.items);
        cursor = page.next_cursor ?? undefined;
      } while (cursor && all.length < 5000);
      return all;
    },
    documentList: (q) => unwrap(api.GET('/documents/list', { params: { header: org(), query: q } })),
    documents: async (q) =>
      (await unwrap(api.GET('/documents', {
        params: { header: org(), query: { type: q.type, status: q.status, overdue: q.overdue, contact_id: q.contactId, limit: 500 } },
      }))).items,
    document: (id) => unwrap(api.GET('/documents/{id}', { params: doc(id) })),
    contactTransactions: (id, q = {}) => unwrap(api.GET('/contacts/{id}/transactions', { params: { header: org(), path: { id }, query: q } })),
    contact: (id) => unwrap(api.GET('/contacts/{id}', { params: doc(id) })),
    createContact: (input) => unwrap(api.POST('/contacts', { params: { header: org() }, body: input })),
    updateContact: (id, input) => unwrap(api.PATCH('/contacts/{id}', { params: doc(id), body: input })),
    importContacts: (req) => unwrap(api.POST('/imports/contacts', { params: { header: org() }, body: req })),
    checkOpenItems: (input) => unwrap(api.POST('/imports/open-items', { params: { header: org() }, body: input })),
    checkInvoiceImport: (input) => unwrap(api.POST('/imports/invoices', { params: { header: org() }, body: input })),
    invoiceMappings: () => unwrap(api.GET('/imports/invoice-mappings', { params: { header: org() } })),
    saveInvoiceMapping: (input) => unwrap(api.POST('/imports/invoice-mappings', { params: { header: org() }, body: input })),
    deleteInvoiceMapping: async (id) => { await unwrap(api.DELETE('/imports/invoice-mappings/{id}', { params: doc(id) })); },
    recordReceipt: (input) => unwrap(api.POST('/receipts', { params: { header: org() }, body: input })),
    recordPayment: (input) => unwrap(api.POST('/payments', { params: { header: org() }, body: input })),
    onAccount: (id) => unwrap(api.GET('/contacts/{id}/on-account', { params: { header: org(), path: { id } } })),
    cashDocument: (id) => unwrap(api.GET('/cash/{id}', { params: { header: org(), path: { id } } })),
    reallocateCash: (id, allocations, reason) => unwrap(api.PUT('/cash/{id}/allocations', { params: { header: org(), path: { id } }, body: { allocations, reason: reason ?? null } })),
    undoCash: (id, date, reason) => unwrap(api.POST('/cash/{id}/undo', { params: doc(id), body: { date, reason } })),
    refundOnAccount: (id, input) => unwrap(api.POST('/cash/{id}/refund', { params: doc(id), body: input })),
    allocateCredit: (id, allocations) => unwrap(api.POST('/documents/{id}/allocations', { params: doc(id), body: { allocations } })),
    trialBalance: (asAt) => unwrap(api.GET('/reports/trial-balance', { params: { header: org(), query: { as_at: asAt } } })),
    balanceSheet: (asAt) => unwrap(api.GET('/reports/balance-sheet', { params: { header: org(), query: { as_at: asAt } } })),
    agedCreditors: (asAt) =>
      unwrap(api.GET('/reports/aged', { params: { header: org(), query: { kind: 'creditors', as_at: asAt } } })),
    createDraft: (input) => unwrap(api.POST('/documents', { params: { header: org() }, body: input })),
    replaceDraft: (id, input) => unwrap(api.PUT('/documents/{id}', { params: doc(id), body: input })),
    deleteDraft: async (id) => {
      await unwrap(api.DELETE('/documents/{id}', { params: doc(id) }));
    },
    postDocument: (id, paidFrom) => unwrap(api.POST('/documents/{id}/post', { params: doc(id), body: paidFrom ? { paid_from: paidFrom } : {} })),
    voidDocument: (id, date, reason) => unwrap(api.POST('/documents/{id}/void', { params: doc(id), body: { date, reason } })),
    creditAndReissue: (id, date, reason) => unwrap(api.POST('/documents/{id}/credit-and-reissue', { params: doc(id), body: { date, reason } })),
    sendDocument: (id, input) => unwrap(api.POST('/documents/{id}/send', { params: doc(id), body: input })),
    templates: () => unwrap(api.GET('/templates', { params: { header: org() } })),
    saveTemplate: (kind, layout, settings) =>
      unwrap(api.PUT('/templates/{kind}', { params: { header: org(), path: { kind } }, body: { layout, settings } })),
    createTemplate: (name, copyFrom) => unwrap(api.POST('/templates', { params: { header: org() }, body: { name, copy_from: copyFrom ?? null } })),
    updateTemplate: (id, patch) => unwrap(api.PUT('/templates/layouts/{id}', { params: doc(id), body: patch })),
    deleteTemplate: async (id) => { await unwrap(api.DELETE('/templates/layouts/{id}', { params: doc(id) })); },
    // Fetched through the client so the bearer token and org header go with it.
    inbox: (status) => unwrap(api.GET('/inbox', { params: { header: org(), query: { status } } })),
    inboxAddress: () => unwrap(api.GET('/inbox/address', { params: { header: org() } })),
    newInboxAddress: (allowedSenders) => unwrap(api.POST('/inbox/address', { params: { header: org() }, body: { allowed_senders: allowedSenders } })),
    inboxItem: (id) => unwrap(api.GET('/inbox/{id}', { params: doc(id) })),
    setInboxSupplier: (id, choice) => unwrap(api.PUT('/inbox/{id}/supplier', { params: doc(id), body: choice })),
    approveInbox: (id, ack) => unwrap(api.POST('/inbox/{id}/approve', { params: doc(id), body: { acknowledge_warnings: ack } })),
    rejectInbox: async (id, reason) => {
      await unwrap(api.POST('/inbox/{id}/reject', { params: doc(id), body: { reason } }));
    },
    retryInbox: (id) => unwrap(api.POST('/inbox/{id}/retry', { params: doc(id) })),
    attachmentFile: async (id) => (await unwrap(api.GET('/attachments/{id}/file', { params: doc(id), parseAs: 'blob' }))) as unknown as Blob,
    uploadAttachment: (file, target = {}) => {
      const form = new FormData();
      form.append('file', file);
      if (target.to_inbox !== undefined) form.append('to_inbox', String(target.to_inbox));
      if (target.document_id) form.append('document_id', target.document_id);
      if (target.journal_id) form.append('journal_id', target.journal_id);
      return unwrap(api.POST('/attachments', {
        params: { header: org() },
        body: { file: file as unknown as string, ...target },
        bodySerializer: () => form,
      }));
    },
    unlinkAttachment: async (id, documentId) => {
      await unwrap(api.DELETE('/attachments/{id}/link', { params: { ...doc(id), query: { document_id: documentId } } }));
    },
    brandAssets: () => unwrap(api.GET('/brand-assets', { params: { header: org() } })),
    uploadBrandAsset: (file, kind) => {
      const form = new FormData();
      form.append('file', file);
      form.append('kind', kind);
      return unwrap(api.POST('/brand-assets', {
        params: { header: org() },
        body: { file: file as unknown as string, kind },
        bodySerializer: () => form,
      }));
    },
    brandAssetFile: async (id) => (await unwrap(api.GET('/brand-assets/{id}/file', { params: doc(id), parseAs: 'blob' }))) as unknown as Blob,
    setBankDetails: (code, details) =>
      unwrap(api.PUT('/bank-accounts/{code}/details', { params: { header: org(), path: { code } }, body: details })),

    bankFeeds: () => unwrap(api.GET('/bank-feeds', { params: { header: org() } })),
    feedInstitutions: (country) => unwrap(api.GET('/bank-feeds/institutions', { params: { header: org(), query: { country } } })),
    connectBankFeed: (institution, country) => unwrap(api.POST('/bank-feeds/connect', { params: { header: org() }, body: { institution, country } })),
    linkBankFeedAccount: (id, accountCode) => unwrap(api.PUT('/bank-feeds/accounts/{id}', { params: doc(id), body: { account_code: accountCode } })),
    syncBankFeeds: () => unwrap(api.POST('/bank-feeds/sync', { params: { header: org() } })),
    disconnectBankFeed: async (id) => {
      await unwrap(api.DELETE('/bank-feeds/{id}', { params: doc(id) }));
    },

    bankLines: (code, status) => unwrap(api.GET('/bank-accounts/{code}/lines', { params: { header: org(), path: { code }, query: { status } } })),
    bankTransactions: (code, q = {}) => unwrap(api.GET('/bank-accounts/{code}/transactions', { params: { header: org(), path: { code }, query: q } })),
    reconcileByHand: (code, input) => unwrap(api.POST('/bank-accounts/{code}/reconcile-by-hand', { params: { header: org(), path: { code } }, body: input })),
    untickByHand: async (code, id, reason) => { await unwrap(api.POST('/bank-accounts/{code}/transactions/{id}/untick', { params: { header: org(), path: { code, id } }, body: { reason } })); },
    bankSuggestions: (id) => unwrap(api.GET('/bank-lines/{id}/suggestions', { params: doc(id) })),
    reconcile: (id, action) => unwrap(api.POST('/bank-lines/{id}/reconcile', { params: doc(id), body: action })),
    unmatch: (id) => unwrap(api.POST('/bank-lines/{id}/unmatch', { params: doc(id) })),
    reconciliation: (code, asAt) =>
      unwrap(api.GET('/bank-accounts/{code}/reconciliation', { params: { header: org(), path: { code }, query: { as_at: asAt } } })),
    signOff: (code, asAt) =>
      unwrap(api.POST('/bank-accounts/{code}/reconciliation', { params: { header: org(), path: { code }, query: { as_at: asAt } } })),
    bankRules: () => unwrap(api.GET('/bank-rules', { params: { header: org() } })),
    createBankRule: (rule) => unwrap(api.POST('/bank-rules', { params: { header: org() }, body: rule })),
    uploadStatement: (code, file) => {
      const form = new FormData();
      form.append('file', file);
      return unwrap(api.POST('/bank-accounts/{code}/statements', {
        params: { header: org(), path: { code } },
        body: { file: file as unknown as string },
        bodySerializer: () => form,
      }));
    },
    vatReturns: () => unwrap(api.GET('/vat/returns', { params: { header: org() } })),
    calculateVat: (from, to) => unwrap(api.GET('/vat/returns/calculate', { params: { header: org(), query: { type: 'IE_VAT3', from, to } } })),
    rosVat3: (from, to) => unwrap(api.GET('/vat/ros/vat3', { params: { header: org(), query: { from, to } } })),
    rosVat3Csv: async (from, to) =>
      (await unwrap(api.GET('/vat/ros/vat3', { params: { header: org(), query: { from, to, format: 'csv' } }, parseAs: 'blob' }))) as unknown as Blob,
    fileVat: (from, to, reference) => unwrap(api.POST('/vat/returns/file', { params: { header: org() }, body: { type: 'IE_VAT3', from, to, reference } })),

    fiscalYears: () => unwrap(api.GET('/fiscal-years', { params: { header: org() } })),
    exportSaft: async (from, to) =>
      (await unwrap(api.GET('/exports/saft', { params: { header: org(), query: { from, to } }, parseAs: 'blob' }))) as unknown as Blob,
    exportAccountantPack: async (from, to) =>
      (await unwrap(api.GET('/exports/accountant-pack', { params: { header: org(), query: { from, to } }, parseAs: 'blob' }))) as unknown as Blob,
    bookExports: () => unwrap(api.GET('/exports', { params: { header: org() } })),
    search: (q, limit) => unwrap(api.GET('/search', { params: { header: org(), query: { q, limit } } })),
    status: () => unwrap(api.GET('/status', { params: { header: org() } })),
    yearEndChecklist: (id) => unwrap(api.GET('/fiscal-years/{id}/checklist', { params: doc(id) })),
    closeYear: (id, ack) => unwrap(api.POST('/fiscal-years/{id}/close', { params: doc(id), body: { acknowledge_warnings: ack } })),
    reopenYear: (id, reason) => unwrap(api.POST('/fiscal-years/{id}/reopen', { params: doc(id), body: { reason } })),
    setPeriodStatus: (id, status) => unwrap(api.PUT('/periods/{id}/status', { params: doc(id), body: { status } })),
    monthEndChecklist: (id) => unwrap(api.GET('/periods/{id}/checklist', { params: doc(id) })),
    closePeriod: (id, ack) => unwrap(api.POST('/periods/{id}/close', { params: doc(id), body: { acknowledge_warnings: ack } })),
    auditTrail: (f) => unwrap(api.GET('/audit', { params: { header: org(), query: { ...f } } })),
    auditPeople: () => unwrap(api.GET('/audit/people', { params: { header: org() } })),

    createStatements: (req) => unwrap(api.POST('/statements', { params: { header: org() }, body: req })),
    statementSchedules: () => unwrap(api.GET('/statement-schedules', { params: { header: org() } })),
    createStatementSchedule: (sch) => unwrap(api.POST('/statement-schedules', { params: { header: org() }, body: sch })),
    recurring: () => unwrap(api.GET('/recurring', { params: { header: org() } })),
    createRecurring: (t) => unwrap(api.POST('/recurring', { params: { header: org() }, body: t })),
    runRecurringNow: (id) => unwrap(api.POST('/recurring/{id}/run-now', { params: doc(id) })),
    recurringRuns: (status) => unwrap(api.GET('/recurring-runs', { params: { header: org(), query: { status } } })),
    approveRun: (id, send) => unwrap(api.POST('/recurring-runs/{id}/approve', { params: doc(id), body: { send } })),
    declineRun: (id, skip, note) => unwrap(api.POST('/recurring-runs/{id}/decline', { params: doc(id), body: { skip, note } })),

    members: () => unwrap(api.GET('/members', { params: { header: org() } })),
    invite: (inv) => unwrap(api.POST('/invitations', { params: { header: org() }, body: inv })),
    revokeInvitation: async (id) => {
      await unwrap(api.DELETE('/invitations/{id}', { params: doc(id) }));
    },

    imports: () => unwrap(api.GET('/imports', { params: { header: org() } })),
    importBatch: (id) => unwrap(api.GET('/imports/{id}', { params: doc(id) })),
    discardImport: async (id) => { await unwrap(api.DELETE('/imports/{id}', { params: doc(id) })); },
    setAccountMap: (id, map) => unwrap(api.PUT('/imports/{id}/account-map', { params: doc(id), body: map })),
    validateImport: (id) => unwrap(api.POST('/imports/{id}/validate', { params: doc(id) })),
    commitImport: (id, create) => unwrap(api.POST('/imports/{id}/commit', { params: doc(id), body: { create_missing_contacts: create } })),
    reverseImport: (id, reason) => unwrap(api.POST('/imports/{id}/reverse', { params: doc(id), body: { reason } })),
    compareWithSage: (id, input) => unwrap(api.POST('/imports/{id}/compare', { params: doc(id), body: input })),
    uploadImport: (file, entity, sourceFormat, asAt, profitToCapital) => {
      const form = new FormData();
      form.append('file', file);
      form.append('entity', entity);
      form.append('source_format', sourceFormat);
      if (asAt) form.append('as_at', asAt);
      if (profitToCapital !== undefined) form.append('profit_to_capital', String(profitToCapital));
      return unwrap(api.POST('/imports', {
        params: { header: org() },
        body: { file: file as unknown as string, entity, source_format: sourceFormat as 'csv_template' },
        bodySerializer: () => form,
      }));
    },
    sageStatus: () => unwrap(api.GET('/imports/sage', { params: { header: org() } })),
    sageConnect: () => unwrap(api.POST('/imports/sage/connect', { params: { header: org() } })),
    sageDisconnect: async () => {
      await unwrap(api.DELETE('/imports/sage', { params: { header: org() } }));
    },
    sagePull: (asAt) => unwrap(api.POST('/imports/sage/pull', { params: { header: org() }, body: { as_at: asAt } })),

    backups: () => unwrap(api.GET('/backups', { params: { header: org() } })),
    requestBackup: (reason) => unwrap(api.POST('/backups', { params: { header: org() }, body: { reason } })),
    downloadBackup: async (id) => (await unwrap(api.GET('/backups/{id}/download', { params: doc(id), parseAs: 'blob' }))) as unknown as Blob,
    restoreBackup: (id, choice) => unwrap(api.POST('/backups/{id}/restore', { params: doc(id), body: choice })),

    fxOverview: (asAt) => unwrap(api.GET('/fx', { params: { header: org(), query: { as_at: asAt } } })),
    setFxSettings: (settings) => unwrap(api.PUT('/fx/settings', { params: { header: org() }, body: settings })),
    fxRate: (currency, date) => unwrap(api.GET('/fx/rate', { params: { header: org(), query: { currency, date } } })),
    setFxRate: async (currency, date, rate) => {
      await unwrap(api.PUT('/fx/rates', { params: { header: org() }, body: { currency, date, rate } }));
    },
    connectFxFeed: (credentials) => unwrap(api.PUT('/fx/feed', { params: { header: org() }, body: credentials })),
    disconnectFxFeed: async () => {
      await unwrap(api.DELETE('/fx/feed', { params: { header: org() } }));
    },
    revaluationPreview: (asAt) => unwrap(api.GET('/fx/revaluation', { params: { header: org(), query: { as_at: asAt } } })),
    postRevaluation: (asAt) => unwrap(api.POST('/fx/revaluation', { params: { header: org() }, body: { as_at: asAt } })),

    hmrcStatus: () => unwrap(api.GET('/vat/hmrc', { params: { header: org() } })),
    connectHmrc: () => unwrap(api.POST('/vat/hmrc/connect', { params: { header: org() } })),
    disconnectHmrc: async () => {
      await unwrap(api.DELETE('/vat/hmrc', { params: { header: org() } }));
    },
    // HMRC's fraud prevention headers need the browser's details on every call that reaches HMRC.
    mtdObligations: () => unwrap(api.GET('/vat/hmrc/obligations', { params: { header: { ...org(), 'Elysian-Client-Info': clientInfoHeader() } } })),
    submitMtdReturn: (periodKey, from, to) => unwrap(api.POST('/vat/hmrc/returns', {
      params: { header: { ...org(), 'Elysian-Client-Info': clientInfoHeader() } },
      body: { period_key: periodKey, from, to, confirmed: true },
    })),
    mtdReceipt: (periodKey) => unwrap(api.GET('/vat/hmrc/returns/{periodKey}', { params: { header: org(), path: { periodKey } } })),

    pdf: async (id) => (await unwrap(api.GET('/documents/{id}/preview', { params: { ...doc(id), query: {} }, parseAs: 'blob' }))) as unknown as Blob,
  };
}

/** The API when VITE_API_URL is set (the session supplies the token and organisation); demo data otherwise. */
export function defaultSource(getToken: () => Promise<string | null>, getOrgId: () => string | null, onUnauthorized: () => void): LedgerSource {
  const url = import.meta.env.VITE_API_URL as string | undefined;
  return url ? apiSource(url, getToken, getOrgId, onUnauthorized) : mock;
}
